import React, { Component } from 'react';
import 'url-search-params-polyfill';
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.css';
import './Root.scss';
import UserPrefsContext from '../../../context/UserPrefs/UserPrefsContext';
import PageLayout from '../../components/PageLayout/PageLayout';
import { SALES_TAX } from '../../data/assumptions/ASSUMPTIONS';

import experiencePreviewImage from '../../assets/images/experience-preview.png';
import metaImage from '../../assets/images/metaImage.png';
import eventsScreenshot from '../../assets/images/metaImage.png';

// Pages
import Home from '../../components/HomePage/Homepage';
import EVs from '../../../pages/EVs/EVs';
import EVsGraph from '../../../pages/EVs/EVsGraph';
import EV from '../../../pages/EV/EV';
import UsedEV from '../../../pages/UsedEV/UsedEV';
import Incentives from '../../../pages/Incentives/Incentives';
import MapPage from '../../pages/MapPage/MapPage';
import FAQ from '../../../pages/FAQ/FAQ';
import FourOhFour from '../../../pages/FourOhFour/FourOhFour';
import CompareVehicles from '../../components/CompareVehicles/CompareVehicles';
import HomeChargers from '../../../pages/HomeChargers/HomeChargers';
import Inventory from '../../../pages/Inventory/Inventory';
import Events from '../../../pages/Events/Events';
import CPAdditionalInfo from '../../components/ChargingPerksPilot/CPAdditionalInfo';
import RateComparison from '../../pages/RateComparison/RateComparison';
import Emissions from '../../pages/Emissions/Emissions';

// Services
import fetchWrapper from '../../../utils/fetch/fetchWrapper';
import fetchElectricVehicles from '../../../services/fetchElectricVehicles';
import fetchGasolineVehicles from '../../../services/fetchGasolineVehicles';
import fetchIncentives from '../../../services/fetchIncentives';
import fetchVehicleIncentivesWithHandle from '../../../services/fetchVehicleIncentivesWithHandle';
import fetchHomeChargers from '../../../services/fetchHomeChargers';
import fetchFaqs from '../../../services/fetchFaqs';

// Utilities
import Uuid from '../../../utils/Uuid/Uuid';
import {
  loadState,
  persistState,
} from '../../../utils/LocalStorage/LocalStorage';
import loadUserPrefs from '../../../context/UserPrefs/loadUserPrefs';
import getUserPref from '../../../context/UserPrefs/getUserPref';
import USER_PREF_PRESETS from '../../../context/UserPrefs/USER_PREF_PRESETS';
import GaTracker from '../../../utils/GaTracker/GaTracker';
import isIE from '../../../utils/isIE';
import { MAP_TABS } from '../../../constants/mapTabs';
import {
  getCookie,
  parseLocationPrefs,
  prefsChanged,
} from './rootHelpers';

//Layouts
import ContentCenter from '../../components/PageLayout/BodyTemplates/ContentCenter';
import FrequentlyAskedQuestionsSection from '../../components/FrequentlyAskedQuestionsSection/FrequentlyAskedQuestionsSection';

//Hero Images
import chargingPerks from '../../assets/images/hero/chargingPerks.png';
import ChargingPerksPilot from '../../components/ChargingPerksPilot/ChargingPerksPilot';
import incomeQualifiedHero from '../../assets/images/hero/incomeQualified.jpg';
import IncomeQualified from '../../components/IncomeQualified/IncomeQualified';

import { FormattedMessage } from 'react-intl';
import {
  CHANGING_PERKS_PILOT_COLORADO,
  INCOME_QUALIFIED_COLORADO,
} from '../../../constants';

const ENABLED_MAP_TABS = MAP_TABS.filter((tab) => tab.isEnabled);
// Home and vehicle detail pages embed a map; they use the first enabled tab.
const PRIMARY_MAP_TAB_ID = ENABLED_MAP_TABS[0]?.id;

const HOUSEHOLD_PREF_KEYS = [
  'householdSize',
  'householdIncome',
  'canTurnInClunker',
  'taxFilingStatus',
];
const INCENTIVE_VEHICLE_PREF_KEYS = [
  'vehicleIdForIncentives',
  'vehicleHandleForIncentives',
  'vehicleCondition',
];
const FILTER_PREF_KEYS = [
  'vehicleFormFactorFilters',
  'vehicleFuelTypeFilters',
  'chargerTypeFilters',
  'chargerFormFactorFilters',
  'chargerWifiFilter',
];

const canPersist = (state) =>
  !state.userLocationNotFound && !state.zipcodeUpdating;

// Keep whatever data we already have; only replace "still loading" (null)
// with an empty list so pages stop showing a spinner after a failure.
const emptyIfMissing = (...keys) => (state) =>
  Object.fromEntries(keys.map((key) => [key, state[key] ?? []]));

class Root extends Component {
  constructor(props) {
    super(props);

    const existingState = loadState() || {};
    const savedPrefs = { ...(existingState.userPreferences || {}) };
    FILTER_PREF_KEYS.forEach((key) => {
      if (!savedPrefs[key]) savedPrefs[key] = {};
    });

    const userPreferences = loadUserPrefs(savedPrefs);

    // A zip from the site's post-code cookie wins over the saved one, unless
    // the user is mid-way through entering a new zip. Applied here (rather
    // than after mount) so the first round of requests uses the right zip.
    const cookieZip = getCookie('post-code');
    if (cookieZip && !getUserPref('workingZipcode', userPreferences)) {
      userPreferences.zipcode = cookieZip;
    }

    this.state = {
      uuid: existingState.uuid || Uuid(),
      ipData: existingState.ipData || null,
      electricVehicles: null,
      usedElectricVehicles: null,
      gasolineVehicles: null,
      incentives: null,
      incentivePrefsModalIsOpen: false,
      userLocation: null,
      userLocationNotFound: false,
      zipcodeUpdating: false,
      homeChargers: null,
      faqs: null,
      userPreferences,
    };

    // Latest request id per loader, used to drop out-of-order responses.
    this.latestRequest = {};
    this.userPrefsContext = null;

    this.loadElectricVehicleData = this.loadElectricVehicleData.bind(this);
    this.loadGasolineVehicleData = this.loadGasolineVehicleData.bind(this);
    this.loadIncentivesData = this.loadIncentivesData.bind(this);
    this.updateUserPreferences = this.updateUserPreferences.bind(this);
    this.incentivePrefsModalToggle = this.incentivePrefsModalToggle.bind(this);
  }

  componentDidMount() {
    this.loadElectricVehicleData();
    this.loadGasolineVehicleData();
    this.loadZipcodeData();
    this.loadIncentivesData();
    this.loadHomeChargers();
    this.loadFaqs();
    // Dealers load once the location resolves (see componentDidUpdate).

    GaTracker.initialize();
    const page = this.props.location.pathname + this.props.location.search;
    GaTracker.trackPage(page, { userId: this.state.uuid });
  }

  componentDidUpdate(prevProps, prevState) {
    const prefs = this.state.userPreferences;
    const prevPrefs = prevState.userPreferences;

    const languageChanged = this.props.language !== prevProps.language;
    const zipChanged = prefs.zipcode !== prevPrefs.zipcode;
    const householdChanged = prefsChanged(prevPrefs, prefs, HOUSEHOLD_PREF_KEYS);
    const incentiveVehicleChanged = prefsChanged(
      prevPrefs,
      prefs,
      INCENTIVE_VEHICLE_PREF_KEYS
    );

    if (zipChanged || householdChanged || languageChanged) {
      this.loadElectricVehicleData();
    }
    if (
      zipChanged ||
      householdChanged ||
      incentiveVehicleChanged ||
      languageChanged
    ) {
      this.loadIncentivesData();
    }
    if (zipChanged || languageChanged) {
      this.loadGasolineVehicleData();
    }
    if (zipChanged) {
      this.loadZipcodeData();
    }
    if (zipChanged || prefs.chargerWifiFilter !== prevPrefs.chargerWifiFilter) {
      this.loadHomeChargers();
    }

    const persistedFieldsChanged =
      prefs !== prevPrefs ||
      this.state.uuid !== prevState.uuid ||
      this.state.ipData !== prevState.ipData;
    if (
      canPersist(this.state) &&
      (persistedFieldsChanged || !canPersist(prevState))
    ) {
      const { uuid, userPreferences, ipData } = this.state;
      persistState({ uuid, userPreferences, ipData });
    }

    const currentPage = prevProps.location.pathname + prevProps.location.search;
    const nextPage = this.props.location.pathname + this.props.location.search;
    if (currentPage !== nextPage) {
      GaTracker.trackPage(nextPage, { userId: this.state.uuid });
    }
  }

  // Returns a function that reports whether this is still the newest request
  // for `name`, so slow responses can't overwrite fresher data.
  startRequest(name) {
    const id = (this.latestRequest[name] || 0) + 1;
    this.latestRequest[name] = id;
    return () => this.latestRequest[name] === id;
  }

  async load(name, request, toState, onErrorState) {
    const isLatest = this.startRequest(name);
    try {
      const result = await request();
      if (isLatest() && result) this.setState(toState(result));
    } catch (error) {
      if (!isLatest()) return;
      console.error(`Failed to load ${name}:`, error);
      if (onErrorState) this.setState(onErrorState);
    }
  }

  getPref(key) {
    return getUserPref(key, this.state.userPreferences);
  }

  get apiLanguage() {
    return this.props.language === 'EN' ? null : this.props.language;
  }

  loadElectricVehicleData() {
    const params = {
      postcode: this.getPref('zipcode'),
      household_size: this.getPref('householdSize'),
      household_income: this.getPref('householdIncome'),
      lang: this.apiLanguage,
    };

    return this.load(
      'electric vehicles',
      () => fetchElectricVehicles(params),
      (result) => ({
        electricVehicles: result.newElectricVehicles,
        usedElectricVehicles: result.usedElectricVehicles,
      }),
      emptyIfMissing('electricVehicles', 'usedElectricVehicles')
    );
  }

  loadGasolineVehicleData() {
    const params = {
      fuel_type: 'gas',
      postcode: this.getPref('zipcode'),
      lang: this.apiLanguage,
    };

    return this.load(
      'gasoline vehicles',
      () => fetchGasolineVehicles(params),
      (gasolineVehicles) => ({ gasolineVehicles }),
      emptyIfMissing('gasolineVehicles')
    );
  }

  loadFaqs() {
    return this.load(
      'faqs',
      () => fetchFaqs(),
      (faqs) => ({ faqs })
    );
  }

  loadIncentivesData() {
    const vehicleHandle = this.getPref('vehicleHandleForIncentives');
    const vehicleCondition = this.getPref('vehicleCondition');

    const params = {
      postcode: this.getPref('zipcode'),
      vehicle_handle: vehicleHandle,
      household_size: this.getPref('householdSize'),
      household_income: this.getPref('householdIncome'),
      turn_in_clunker: this.getPref('canTurnInClunker') ? true : null,
      tax_filing_type: this.getPref('taxFilingStatus'),
      lang: this.apiLanguage,
      treat_as: vehicleCondition,
    };
    if (vehicleCondition === 'used') {
      params.include_used_vehicles = true;
    }

    return this.load(
      'incentives',
      () =>
        vehicleHandle
          ? fetchVehicleIncentivesWithHandle(params)
          : fetchIncentives(params),
      (incentives) => ({ incentives }),
      emptyIfMissing('incentives')
    );
  }

  loadHomeChargers() {
    const params = { postcode: this.getPref('zipcode') };

    const wifiPrefs = this.getPref('chargerWifiFilter') || {};
    if (wifiPrefs.yes) {
      params.wifi = 'true';
    } else if (wifiPrefs.no) {
      params.wifi = 'false';
    }

    return this.load(
      'home chargers',
      () => fetchHomeChargers(params),
      (homeChargers) => ({ homeChargers }),
      emptyIfMissing('homeChargers')
    );
  }

  loadZipcodeData() {
    const isLatest = this.startRequest('location');
    const url = new URL(`${process.env.REACT_APP_API_HOST}/location`);
    url.search = new URLSearchParams({
      postcode: this.getPref('zipcode'),
      nearby_distance: isIE() ? 25 : 100,
    });

    return fetchWrapper(url, { method: 'GET' })
      .then((data) => {
        if (!isLatest()) return;
        if (!data?.location) throw new Error('Location not found');

        this.setState({
          userLocation: data.location,
          userLocationNotFound: false,
          zipcodeUpdating: false,
          incentivePrefsModalIsOpen: false,
        });
        this.updateUserPreferences(
          parseLocationPrefs(data.location, {
            defaultSalesTax: SALES_TAX.value,
          })
        );
      })
      .catch(() => {
        if (!isLatest()) return;
        this.setState({ zipcodeUpdating: false, userLocationNotFound: true });
      });
  }

  updateUserPreferences(newPrefs) {
    this.setState((state) => {
      const zipChanged =
        newPrefs.zipcode && newPrefs.zipcode !== state.userPreferences.zipcode;
      return {
        userPreferences: { ...state.userPreferences, ...newPrefs },
        ...(zipChanged ? { zipcodeUpdating: true } : {}),
      };
    });
  }

  incentivePrefsModalToggle(override) {
    this.setState((state) => ({
      incentivePrefsModalIsOpen:
        typeof override === 'boolean'
          ? override
          : !state.incentivePrefsModalIsOpen,
    }));
  }

  // Rebuilt only when something it exposes changes, so context consumers
  // don't re-render every time unrelated data (vehicles, dealers...) loads.
  getUserPrefsContext() {
    const {
      userPreferences,
      userLocationNotFound,
      zipcodeUpdating,
      incentivePrefsModalIsOpen,
    } = this.state;
    const deps = [
      userPreferences,
      userLocationNotFound,
      zipcodeUpdating,
      incentivePrefsModalIsOpen,
    ];
    const cached = this.userPrefsContext;
    if (cached && cached.deps.every((dep, i) => dep === deps[i])) {
      return cached.value;
    }

    const value = {
      get: (key) => getUserPref(key, userPreferences),
      getPreset: (key) => USER_PREF_PRESETS[key],
      set: this.updateUserPreferences,
      zipcodeIsNotFound: userLocationNotFound,
      zipcodeIsUpdating: zipcodeUpdating,
      showIncentivePrefsModal: incentivePrefsModalIsOpen,
      toggleIncentivePrefsModal: this.incentivePrefsModalToggle,
      syncWorkingZipcode: () =>
        this.updateUserPreferences({
          zipcode: this.getPref('workingZipcode'),
        }),
    };
    this.userPrefsContext = { deps, value };
    return value;
  }

  // Wraps a page in PageLayout with the props every page shares.
  renderPage(layoutProps, page) {
    return (
      <PageLayout
        language={this.props.language}
        changeLanguage={this.props.changeLanguage}
        zipcode={this.getPref('zipcode')}
        userLocation={this.state.userLocation}
        electricVehicles={this.state.electricVehicles}
        usedElectricVehicles={this.state.usedElectricVehicles}
        {...layoutProps}
      >
        {page}
      </PageLayout>
    );
  }

  render() {
    const {
      uuid,
      userLocation,
      electricVehicles,
      usedElectricVehicles,
      gasolineVehicles,
      incentives,
      userPreferences,
      faqs,
    } = this.state;
    const ip = this.state.ipData ? this.state.ipData.ip : null;
    const zipcode = this.getPref('zipcode');
    const region = userLocation?.region;

    return (
      <UserPrefsContext.Provider value={this.getUserPrefsContext()}>
        <Routes>
          <Route
            path="/"
            element={this.renderPage(
              {
                page: 'Home | Dominion Energy EV Shopper',
                title: 'Dominion Energy',
                description:
                  'Learn more about electric vehicles (EVs) incentives',
                image: metaImage,
              },
              <Home
                electricVehicles={electricVehicles}
                tabId={PRIMARY_MAP_TAB_ID}
                incentives={incentives}
                userPreferences={userPreferences}
                userLocation={userLocation}
                ip={ip}
                uuid={uuid}
                title="Dominion Energy"
                faqs={faqs}
              />
            )}
          />
          {process.env.REACT_APP_PAGES_FAQ_ENABLED && (
            <Route
              path="/ev-101"
              element={this.renderPage(
                {
                  page: 'FAQ | Dominion Energy EV Shopper',
                  description:
                    'Answers to Frequently Asked Questions about electric vehicles (EVs) and their incentives',
                  activePage: 'faq',
                },
                <FAQ faqs={faqs} />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_VEHICLES_ENABLED && (
            <Route
              path="/vehicles"
              element={this.renderPage(
                {
                  page: 'Vehicles | Dominion Energy EV Shopper',
                  title: 'Vehicles | Dominion Energy EV Shopper',
                  description: '',
                  activePage: 'vehicles',
                },
                <EVs electricVehicles={electricVehicles} ip={ip} uuid={uuid} />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_USED_VEHICLES_ENABLED && (
            <Route
              path="/used-vehicles"
              element={this.renderPage(
                {
                  page: 'vehicles',
                  title: 'Used Vehicles | Dominion Energy EV Shopper',
                  activePage: 'used-vehicles',
                },
                <EVs
                  electricVehicles={usedElectricVehicles}
                  ip={ip}
                  uuid={uuid}
                  zipcode={zipcode}
                  isUsed
                />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_USED_VEHICLES_ENABLED && (
            <Route
              path="/used-vehicles/:evId"
              element={this.renderPage(
                { page: 'vehicles', activePage: 'used-vehicles' },
                <UsedEV
                  electricVehicles={usedElectricVehicles}
                  userLocation={userLocation}
                  ip={ip}
                  uuid={uuid}
                  zipcode={zipcode}
                />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_INCENTIVES_ENABLED && (
            <Route
              path="/incentives"
              element={this.renderPage(
                {
                  page: 'Incentives | Dominion Energy EV Shopper',
                  title: 'Incentives | Dominion Energy EV Shopper',
                  description: '',
                  activePage: 'incentives',
                },
                <Incentives
                  electricVehicles={electricVehicles}
                  incentives={incentives}
                  image={experiencePreviewImage}
                  ip={ip}
                  uuid={uuid}
                  zipcode={zipcode}
                  userLocation={userLocation}
                />
              )}
            />
          )}
          {ENABLED_MAP_TABS.map((tab) => (
            <Route
              key={tab.id}
              path={tab.url}
              element={this.renderPage(
                {
                  page: 'Map | Dominion Energy EV Shopper',
                  description: tab.description,
                  activePage: tab.activePage,
                },
                <MapPage
                  tabId={tab.id}
                  userLocation={userLocation}
                  ip={ip}
                  uuid={uuid}
                  electricVehicles={electricVehicles}
                  usedElectricVehicles={usedElectricVehicles}
                  title={tab.title}
                  zipcode={zipcode}
                />
              )}
            />
          ))}
          <Route
            path="/vehicles/:evId"
            element={this.renderPage(
              {
                page: 'Vehicles | Dominion Energy EV Shopper',
                description:
                  'Compare Electric Vehicles (EVs) by price, range and features.',
                activePage: 'vehicles',
              },
              <EV
                electricVehicles={electricVehicles}
                usedElectricVehicles={usedElectricVehicles}
                tabId={PRIMARY_MAP_TAB_ID}
                userLocation={userLocation}
                ip={ip}
                uuid={uuid}
                evPage={true}
                zipcode={zipcode}
              />
            )}
          />
          {process.env.REACT_APP_PAGES_COMPARE_ENABLED && (
            <Route
              path="/compare-vehicles"
              element={this.renderPage(
                {
                  page: 'Vehicles | Dominion Energy EV Shopper',
                  title: 'Compare Vehicles | Dominion Energy EV Shopper',
                  activePage: 'compare-vehicles',
                },
                <CompareVehicles
                  ip={ip}
                  uuid={uuid}
                  electricVehicles={electricVehicles}
                  gasolineVehicles={gasolineVehicles}
                  userLocation={userLocation}
                  compareUsedVehicles={false}
                  zipcode={zipcode}
                />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_COMPARISON_GRAPH_ENABLED && (
            <Route
              path="/comparison-graph"
              element={this.renderPage(
                { page: 'cost vs range' },
                <EVsGraph
                  electricVehicles={electricVehicles}
                  ip={ip}
                  uuid={uuid}
                  displayGraph={true}
                />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_HOME_CHARGERS_ENABLED && (
            <Route
              path="/home-chargers"
              element={this.renderPage(
                { page: 'home chargers' },
                <HomeChargers />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_INVENTORY_ENABLED && (
            <Route
              path="/inventory"
              element={this.renderPage(
                { page: process.env.REACT_APP_PAGES_INVENTORY_TITLE },
                <Inventory
                  electricVehicles={electricVehicles}
                  userLocation={userLocation}
                  ip={ip}
                  uuid={uuid}
                />
              )}
            />
          )}
          {process.env.REACT_APP_PAGES_EVENTS_ENABLED && (
            <Route
              path="/events"
              element={this.renderPage(
                {
                  page: 'events',
                  title: process.env.REACT_APP_PAGES_EVENTS_TITLE,
                  image: eventsScreenshot,
                },
                <Events ip={ip} uuid={uuid} />
              )}
            />
          )}
          <Route
            path="/charging-perks"
            element={this.renderPage(
              {
                page: 'charging-perks',
                title: 'Charging Perks | Dominion Energy EV Shopper',
                image: eventsScreenshot,
              },
              <>
                <img
                  src={chargingPerks}
                  alt="Hero"
                  style={{ minWidth: '100%', maxWidth: '100%' }}
                />
                <ContentCenter
                  pageHeaderLead="Charging Perks Pilot"
                  pageHeaderHeadLine="Get rewarded for enabling a cleaner, more efficient energy grid"
                  pageHeaderSubHeading="The Xcel Energy Charging Perks pilot program rewards electric vehicle (EV) drivers for charging their vehicles at times when it’s most beneficial to the energy grid. All you have to do is plug in—your automaker or evPulse and Xcel Energy will do the rest. Plus, you can get $100 just for signing up."
                  sectionStyle={{ backgroundColor: '#F6F8FB' }}
                >
                  <ChargingPerksPilot />
                </ContentCenter>
                <ContentCenter
                  pageHeaderLead="FREQUENTLY ASKED QUESTIONS"
                  sectionStyle={{ backgroundColor: '#FFFFFF' }}
                >
                  <FrequentlyAskedQuestionsSection
                    region={region}
                    applicableTo={CHANGING_PERKS_PILOT_COLORADO}
                  />
                  <CPAdditionalInfo />
                </ContentCenter>
              </>
            )}
          />
          <Route
            path="/ev-rebate-co"
            element={this.renderPage(
              {
                page: 'IQ Rebate Page',
                title: 'EV Rebate - Colorado | Dominion Energy EV Shopper',
                image: eventsScreenshot,
              },
              <>
                <img
                  src={incomeQualifiedHero}
                  alt="Hero"
                  style={{ minWidth: '100%', maxWidth: '100%' }}
                />
                <ContentCenter
                  pageHeaderLead={
                    <FormattedMessage
                      id="evRebateCOHeader"
                      defaultMessage="INCOME-QUALIFIED ELECTRIC VEHICLE REBATE"
                      description="EV Rebate CO Header"
                    />
                  }
                  pageHeaderHeadLine={
                    <FormattedMessage
                      id="evRebateCOSubHeader"
                      defaultMessage="Save up to $5,500 on an Electric Vehicle (EV)"
                      description="EV Rebate CO Sub Header"
                    />
                  }
                  pageHeaderSubHeading={
                    <FormattedMessage
                      id="evRebateCOSubHeaderDescription"
                      defaultMessage="Income-qualified customers can receive $3,000 off a used or $5,500 off a new EV when you buy or lease from a Colorado-based car dealer."
                      description="EV Rebate CO Sub Header Description"
                    />
                  }
                  sectionStyle={{ backgroundColor: '#F6F8FB' }}
                >
                  <IncomeQualified />
                </ContentCenter>
                <ContentCenter>
                  <span
                    className="text-center"
                    style={{ display: 'inline-block' }}
                  >
                    If you fill out our application{' '}
                    <strong>before you shop and get pre-qualified,</strong> you
                    can get an <strong>instant, non-taxable rebate</strong> when
                    you buy or lease your EV from a dealer in our{' '}
                    <strong>EV Dealer Network.</strong>
                  </span>
                </ContentCenter>
                <ContentCenter
                  pageHeaderLead={
                    <FormattedMessage
                      id="frequentlyAskedQuestions"
                      defaultMessage="FREQUENTLY ASKED QUESTIONS"
                      description="FREQUENTLY ASKED QUESTIONS"
                    />
                  }
                  sectionStyle={{ backgroundColor: '#FFFFFF' }}
                >
                  <FrequentlyAskedQuestionsSection
                    region={region}
                    applicableTo={INCOME_QUALIFIED_COLORADO}
                  />
                </ContentCenter>
              </>
            )}
          />
          <Route
            path="/compare-used-vehicles"
            element={this.renderPage(
              {
                page: 'Vehicles | Dominion Energy EV Shopper',
                title: 'Compare Used Vehicles | Dominion Energy EV Shopper',
                activePage: 'compare-vehicles',
              },
              <CompareVehicles
                ip={ip}
                uuid={uuid}
                electricVehicles={usedElectricVehicles}
                gasolineVehicles={gasolineVehicles}
                userLocation={userLocation}
                compareUsedVehicles={true}
                zipcode={zipcode}
              />
            )}
          />
          <Route
            path="/rates"
            element={this.renderPage(
              {
                page: 'rates',
                title: 'Drive Electric with Dominion Energy | Rates',
              },
              <RateComparison
                electricVehicles={electricVehicles}
                usedElectricVehicles={usedElectricVehicles}
                userLocation={userLocation}
              />
            )}
          />
          <Route
            path="/emissions"
            element={this.renderPage(
              {
                page: 'emissions',
                title: 'Emissions | Dominion Energy EV Shopper',
              },
              <Emissions />
            )}
          />
          <Route path="*" element={<FourOhFour />} />
        </Routes>
      </UserPrefsContext.Provider>
    );
  }
}

// withRouter replacement for React Router v6
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return (
      <Component
        {...props}
        location={location}
        navigate={navigate}
        params={params}
      />
    );
  }
  return ComponentWithRouterProp;
}

export default withRouter(Root);
